// React
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

// MUI
import { Stack, CircularProgress, Typography } from "@mui/material";

// Components
import {
  ClinicFilter,
  getAvailableClinicSlots,
  hasAppointmentTimePassed,
} from "Components/Appointments/Common";
import { AppointmentCard } from "Common/Components";
import { ConfirmModal } from "Common/Modals/ConfirmModal";
import ViewMoreAppointmentsModal from "Components/Appointments/ViewMoreAppointmentsModal";
import EditAppointmentModal from "Components/Appointments/EditAppointmentModal";

// Redux
import { openSnackbar } from "Store/SnackbarSlice";

// Resources
import { cancelAppointment } from "API/Resource/appointment";

// Constants
import {
  APPOINTMENT_STATUS,
  APPOINTMENT_ACTIVE_STATUS,
} from "Constants/AppointmentConstants";

// Utils
import { getPatientType } from "Utils/PatientManagementUtils";

const PatientHistory = ({
  appointments,
  setAppointments,
  refreshPatientAppointments,
  loading,
  patient,
  isMBS,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Clinics
  const [clinics, setClinics] = useState([]);
  const [clinicSlots, setClinicSlots] = useState(null);
  const [openClinicPicker, setOpenClinicPicker] = useState(false);

  // Appointment
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [newAppointment, setNewAppointment] = useState(null);

  // Modals
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [openConfirmEdit, setOpenConfirmEdit] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [openEditAppointmentModal, setOpenEditAppointmentModal] =
    useState(false);
  const [openViewMoreModal, setOpenViewMoreModal] = useState(false);

  useEffect(() => {
    if (!appointments.length) {
      setFilteredAppointments([]);
      return;
    }

    const currentAppointments = !clinics.length
      ? appointments
      : appointments.filter((appointment) =>
          clinics.some(
            (clinic) => clinic.Clinic.name === appointment.Clinic.name
          )
        );

    const activeAppointments = [];
    const otherAppointments = [];

    currentAppointments.forEach((appointment) => {
      if (APPOINTMENT_ACTIVE_STATUS.includes(appointment.status)) {
        activeAppointments.push(appointment);
      } else {
        otherAppointments.push(appointment);
      }
    });

    setFilteredAppointments([...activeAppointments, ...otherAppointments]);
  }, [appointments, clinics]);

  const handleOpenDeleteModal = (appointment) => {
    setSelectedAppointment(appointment);
    setOpenConfirmDelete(true);
  };

  const handleOpenEditModal = (appointment) => {
    setSelectedAppointment(appointment);
    setOpenConfirmEdit(true);
  };

  const handleOnSuccessEdit = async () => {
    if (refreshPatientAppointments) {
      await refreshPatientAppointments();
    }

    setOpenEditAppointmentModal(false);
    setOpenViewMoreModal(false);
    setSelectedAppointment(null);
    setNewAppointment(null);
  };

  const handleDeleteAppointment = async () => {
    if (!selectedAppointment) {
      return;
    }

    setLoadingModal(true);

    try {
      await cancelAppointment(selectedAppointment.id);

      const updatedAppointments = appointments.map((appointment) =>
        appointment.id === selectedAppointment.id
          ? { ...appointment, status: APPOINTMENT_STATUS.CancelledByMBS }
          : appointment
      );

      setAppointments(updatedAppointments);
      setClinics([]);
    } catch (error) {
      const errorMessages = {
        APPOINTMENT_TIME_PASSED: t(
          "upcomingpastappointments.appointmentTimePassed"
        ),
      };
      const defaultErrorMessage = t("errors.errorCancellingAppointments");
      const errorMessage = errorMessages[error.message] || defaultErrorMessage;

      dispatch(openSnackbar({ message: errorMessage, severity: "error" }));
    } finally {
      setOpenConfirmDelete(false);
      setLoadingModal(false);
    }
  };

  const handleGetClinicAvailableSlots = async () => {
    if (!selectedAppointment || !patient) {
      return;
    }

    try {
      setLoadingModal(true);

      const slots = await getAvailableClinicSlots({
        patient,
        appointment: selectedAppointment,
      });

      setClinicSlots(slots);
      setOpenViewMoreModal(true);
    } catch (error) {
      const errorMessages = {
        RESCHEDULE_TIME_PASSED: t(
          "upcomingpastappointments.rescheduleTimePassed"
        ),
      };
      const defaultErrorMessage = t("errors.errorReschedulingAppointments");
      const errorMessage = errorMessages[error.message] || defaultErrorMessage;

      dispatch(openSnackbar({ message: errorMessage, severity: "error" }));
    } finally {
      setOpenConfirmEdit(false);
      setOpenEditAppointmentModal(false);
      setTimeout(() => {
        setLoadingModal(false);
      }, 200);
    }
  };

  const handleCreateAppointment = (clinic, date, time) => {
    if (!patient || !selectedAppointment) {
      return;
    }

    setNewAppointment({
      patientId: patient.id,
      patientType: getPatientType(patient.inSystem),
      clinicId: clinic.Clinic.id,
      clinicName: t("clinic.name", { clinic: clinic.Clinic }),
      clinicAddress: t("clinic.address", { clinic: clinic.Clinic }),
      date,
      startTime: time.startTime,
      endTime: time.endTime,
      type: selectedAppointment.type,
      notes: selectedAppointment.notes,
      accuroSlots: time.uniqueSlots,
    });
    setOpenEditAppointmentModal(true);
  };

  return (
    <Stack
      spacing={{ xs: 1, sm: 2 }}
      direction="column"
      useFlexGap
      sx={{ pt: "1rem", justifyContent: "center", alignItems: "center" }}
    >
      {appointments.length > 0 && (
        <ClinicFilter
          options={Array.from(
            new Map(
              appointments.map((appointment) => [
                appointment.Clinic.name,
                appointment,
              ])
            ).values()
          )}
          selectedClinics={clinics}
          setSelectedClinics={setClinics}
          open={openClinicPicker}
          setOpenPicker={setOpenClinicPicker}
        />
      )}

      {loading ? (
        <CircularProgress sx={{ mt: "5%", color: "primary.main" }} />
      ) : filteredAppointments.length === 0 ? (
        <Typography align="center" sx={{ py: 2 }}>
          {t("upcomingpastappointments.noAppointments")}
        </Typography>
      ) : (
        filteredAppointments.map((appointment) => (
          <AppointmentCard
            key={appointment.id}
            appointmentSlot={appointment}
            openEditModal={handleOpenEditModal}
            openDeleteModal={handleOpenDeleteModal}
            canModify={isMBS}
            isDrawer
            actionMessage={
              isMBS &&
              hasAppointmentTimePassed(appointment.date, appointment.startTime)
                ? t("mbsAppointments.appointmentInProgressNote")
                : ""
            }
          />
        ))
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        onConfirm={handleDeleteAppointment}
        loading={loadingModal}
        translationKey="confirmDeleteModal"
      />

      {/* Confirm Edit Modal */}
      <ConfirmModal
        open={openConfirmEdit}
        onClose={() => setOpenConfirmEdit(false)}
        onConfirm={handleGetClinicAvailableSlots}
        loading={loadingModal}
        translationKey="confirmAptEditModal"
      />

      {openViewMoreModal && (
        <ViewMoreAppointmentsModal
          clinic={clinicSlots}
          openModal={openViewMoreModal}
          closeModal={() => setOpenViewMoreModal(false)}
          createAppointment={handleCreateAppointment}
        />
      )}

      {openEditAppointmentModal && (
        <EditAppointmentModal
          patient={patient}
          openModal={openEditAppointmentModal}
          closeModal={() => setOpenEditAppointmentModal(false)}
          newAppointment={newAppointment}
          currentAppointment={selectedAppointment}
          refreshAvailableSlots={handleGetClinicAvailableSlots}
          refreshPatientAppointments={refreshPatientAppointments}
          isMBS={isMBS}
          onSuccess={() => handleOnSuccessEdit()}
        />
      )}
    </Stack>
  );
};

export default PatientHistory;
